import { TALENT_PAGE_KEY } from './constants';
import type { TTranslations } from './types';

export const pt: TTranslations = {
  [TALENT_PAGE_KEY]: {
    PAGE_TITLE: 'Talent',
    PAGE_SUBTITLE: 'Analise o desempenho da organização por processo.',
    BREADCRUMB_TALENT: 'Talent',
    FRESHNESS_PREFIX: 'Atualizado',

    TAB_GOALS: 'Metas',
    TAB_COMPETENCIES: 'Competências',

    PROCESS_LOADING: 'Carregando processo',

    PROCESS_ENROLLED: '{{count}} colaborador inscrito',
    PROCESS_ENROLLED_plural: '{{count}} colaboradores inscritos',
    PROCESS_STATUS_IN_PROGRESS: 'Em andamento',
    PROCESS_STATUS_CLOSED: 'Encerrado',

    EMPTY_TITLE: 'Ainda não há processos',
    EMPTY_DESCRIPTION:
      'Quando um processo for criado para este módulo, ele aparecerá aqui.',

    UNAVAILABLE_TITLE: 'Painel não disponível',
    UNAVAILABLE_DESCRIPTION:
      'Sua organização não contratou este módulo de Talent.',

    ERROR_TITLE: 'Não foi possível carregar o painel de Talent',
    ERROR_RETRY: 'Tentar novamente',

    CONTENT_ERROR_TITLE: 'Não foi possível carregar as informações',
    CONTENT_ERROR_DESCRIPTION:
      'Ocorreu um erro ao carregar os dados. Tente novamente.',

    // ANLT-599 — modo Acompanhamento/Resultados
    MODE_TRACKING: 'Acompanhamento',
    MODE_RESULTS: 'Resultados',
    MODE_HINT:
      'Acompanhamento: como está o processo. Resultados: como foi o desempenho.',
    HOWTO_TITLE: 'Como ler este painel',
    HOWTO_PROGRESS_VS_COMPLIANCE:
      'Acompanhamento mede o avanço do processo (quantas metas foram carregadas, aprovadas e reportadas). Resultados mede o desempenho (o quão bem foi).',
    HOWTO_NO_RESULT_IS_NOT_ZERO:
      'Quem não participou não é calculado como zero na média: aparece como «Sem resultado».',
    MODE_TRACKING_PENDING_TITLE: 'Acompanhamento chega em breve',
    MODE_TRACKING_PENDING_DESCRIPTION:
      'Os blocos de Acompanhamento do ciclo de metas ainda não estão disponíveis neste painel.',
    MODE_RESULTS_PENDING_TITLE: 'Resultados chega em breve',
    MODE_RESULTS_PENDING_DESCRIPTION:
      'Os blocos de Resultados do ciclo de metas ainda não estão disponíveis neste painel.',

    // ANLT-599 (task-05) — M-S1: avanço do ciclo por etapa
    CYCLE_HEADING: 'Avanço do ciclo',
    CYCLE_DESCRIPTION:
      'Quantos colaboradores estão em cada etapa do processo de metas.',
    CYCLE_TRACK_MISSING_NOTE:
      'Este processo ainda não tem um ciclo de acompanhamento definido, então a autoavaliação e a avaliação do líder ainda não são mensuráveis.',

    CYCLE_ENROLLED_LABEL: 'Colaboradores inscritos',
    CYCLE_ENROLLED_TOOLTIP:
      'Conta colaboradores atribuídos e ativos hoje. Pode ser maior que o relatório «Resumo de carregamento de metas» (que só conta quem já carregou metas) e diminui com o tempo em um processo encerrado.',
    CYCLE_STAGE_BREAKDOWN: '{{completed}} concluídas · {{pending}} pendentes',
    CYCLE_A11Y_BAND: 'Avanço do ciclo por etapa',

    STAGE_LOADED: 'Metas criadas',
    STAGE_APPROVED: 'Metas aprovadas',
    STAGE_SELF_REPORTED: 'Autoavaliação',
    STAGE_BOSS_REPORTED: 'Avaliação do líder',

    CYCLE_EMPTY_TITLE: 'Ainda não há avanço para mostrar',
    CYCLE_EMPTY_DESCRIPTION:
      'Este processo ainda não tem inscritos ou acompanhamentos carregados.',
    CYCLE_ERROR_TITLE: 'Não foi possível carregar o avanço do ciclo',

    // ANLT-600 (task-02) — M-S2: evolução do avanço
    EVOLUTION_HEADING: 'Evolução do avanço',
    EVOLUTION_DESCRIPTION:
      'Quantos colaboradores concluíram cada etapa, dia a dia do ciclo.',
    EVOLUTION_EMPTY_TITLE: 'Ainda não há dias para desenhar',
    EVOLUTION_EMPTY_DESCRIPTION:
      'Este processo ainda não tem uma janela de datas com acompanhamentos registrados.',
    EVOLUTION_ERROR_TITLE: 'Não foi possível carregar a evolução do avanço',

    // ANLT-600 (task-03) — linha de metadados: janela do processo + data de corte
    EVOLUTION_WINDOW: 'Ciclo {{start}} → {{end}}',
    EVOLUTION_DATA_AS_OF: 'Dados até {{today}}',
    EVOLUTION_TODAY_OUTSIDE:
      'O processo foi encerrado em {{end}}. Os dados estão até {{today}}.',

    // ANLT-600 (task-04) — nota de honestidade (AC-4). Precedência fixa, plano
    // §8: `undefined` e `0` NUNCA imprimem linha (R3). `EVOLUTION_FOOTNOTE_NO_WINDOW`
    // é copy placeholder pendente de confirmação com produto (plano R-2).
    EVOLUTION_FOOTNOTE_TITLE: 'O que a curva não mostra',
    EVOLUTION_FOOTNOTE_UNDRAWN_STAGES:
      'A origem de dados não guarda data para {{stages}}, então essas etapas não podem ser desenhadas. Não são zero: são desconhecidas.',
    EVOLUTION_FOOTNOTE_TRUNCATED:
      'A curva foi cortada em {{lastDay}} pelo limite de pontos da série; o processo continua até {{end}}.',
    EVOLUTION_FOOTNOTE_WITHOUT_DATE:
      '{{self}} colaboradores em autoavaliação e {{boss}} em avaliação do líder concluíram a etapa sem data registrada, então não aparecem na curva.',
    EVOLUTION_FOOTNOTE_AFTER_CURVE:
      '{{self}} em autoavaliação e {{boss}} em avaliação do líder concluíram a etapa depois do último dia da série.',
    EVOLUTION_FOOTNOTE_RECONCILE:
      'Por isso a curva pode terminar abaixo dos cartões de «Avanço do ciclo»: somando o que está acima, os dois números coincidem.',
    EVOLUTION_FOOTNOTE_NO_WINDOW:
      'Ainda não há uma janela de datas para este processo.',
    EVOLUTION_STAGE_LOADED: 'Metas criadas',
    EVOLUTION_STAGE_APPROVED: 'Metas aprovadas',

    // ANLT-600 (task-05) — andaime do gráfico: label do skeleton próprio do
    // gráfico (diferente do Skeleton em nível de seção, task-02) + títulos de
    // eixo. O eixo Y é colaboradores, nunca porcentagem — esta consulta não
    // traz denominador (risco R-1 do plano).
    EVOLUTION_LOADING_LABEL: 'Carregando a evolução do avanço',
    EVOLUTION_AXIS_X: 'Dia do ciclo',
    EVOLUTION_AXIS_Y: 'Colaboradores',

    // ANLT-600 (task-06) — duas séries + marca de «hoje» (AC-2 visual, AC-3).
    // `EVOLUTION_TODAY_MARK` é o `label.value` de `CrehanaReferenceLine`,
    // renderizada somente quando `today` é uma das categorias do eixo.
    EVOLUTION_SERIES_SELF: 'Autoavaliação',
    EVOLUTION_SERIES_BOSS: 'Avaliação do líder',
    EVOLUTION_TODAY_MARK: 'Hoje',

    // ANLT-600 (task-07) — legenda + montagem final. `EVOLUTION_A11Y_CHART` é
    // o `aria-label` do contêiner externo do gráfico (fora de
    // `CrehanaResponsiveContainer`), não um título visual.
    EVOLUTION_A11Y_CHART: 'Gráfico de evolução do avanço do ciclo',

    // ANLT-599 (task-06) — linha de bloqueios, fecha o bloco da história
    BLOCKS_HEADING: 'Bloqueios que travam o ciclo',
    BLOCKS_ALERT: 'Há bloqueios ativos neste processo.',
    BLOCKS_UNAVAILABLE:
      'Não foi possível calcular os bloqueios deste processo.',
    BLOCK_WITHOUT_GOALS: 'Sem metas carregadas',
    BLOCK_WITHOUT_GOALS_HINT:
      'Colaboradores que ainda não carregaram nenhuma meta.',
    BLOCK_NOT_APPROVED: 'Metas sem aprovação',
    BLOCK_NOT_APPROVED_HINT: 'Metas carregadas que o líder ainda não aprovou.',
    BLOCK_WITHOUT_SELF_REPORT: 'Sem autoavaliação',
    BLOCK_WITHOUT_SELF_REPORT_HINT:
      'Colaboradores que ainda não se autoavaliaram.',

    // ANLT-606 (task-07) — aria-label do botão overlay em cada cartão de
    // bloqueio. {{label}} é uma das três chaves BLOCK_* acima.
    BLOCKS_CARD_ACTION: 'Ver os colaboradores em: {{label}}',

    // ANLT-606 — M-S5: detalhe por colaborador
    DETAIL_HEADING: 'Detalhe por colaborador',
    DETAIL_DESCRIPTION:
      'Cada pessoa inscrita, sua etapa e seu avanço no ciclo de metas.',
    DETAIL_TABLE_TITLE: 'Detalhe por colaborador',
    DETAIL_SEARCH_PLACEHOLDER: 'Buscar por nome',
    DETAIL_COL_COLLABORATOR: 'Colaborador',
    DETAIL_COL_HEADQUARTER: 'Sede',
    DETAIL_COL_AREA: 'Área',
    DETAIL_COL_POSITION: 'Cargo',
    DETAIL_COL_BOSS: 'Líder',
    DETAIL_COL_GOALS: 'Metas',
    DETAIL_COL_GOALS_TOOLTIP: 'Metas carregadas pela pessoa.',
    DETAIL_COL_PROGRESS: 'Avanço do ciclo',
    DETAIL_COL_PROGRESS_TOOLTIP:
      'Porcentagem de metas que chegaram à etapa final.',
    DETAIL_COL_STAGE: 'Etapa alcançada',
    DETAIL_COL_STAGE_TOOLTIP:
      'Uma etapa conta como concluída somente quando TODAS as metas da pessoa chegaram a ela.',
    DETAIL_NO_BOSS: 'Sem líder atribuído',
    DETAIL_NO_DATA_SR: 'Sem dado',
    DETAIL_STAGE_NONE: 'Sem metas carregadas',
    DETAIL_STAGE_LOADED: 'Metas criadas',
    DETAIL_STAGE_APPROVED: 'Metas aprovadas',
    DETAIL_STAGE_SELF_REPORTED: 'Autoavaliação',
    DETAIL_STAGE_BOSS_REPORTED: 'Avaliação do líder',
    DETAIL_EMPTY_TITLE: 'Este processo não tem inscritos',
    DETAIL_EMPTY_DESCRIPTION: 'Ainda não há colaboradores neste processo.',
    DETAIL_SEARCH_EMPTY_TITLE: 'Nenhum colaborador corresponde à sua busca',
    DETAIL_SEARCH_EMPTY_DESCRIPTION:
      'Tente outro nome, ou limpe a busca para ver todos os inscritos.',
    DETAIL_ERROR_TITLE: 'Não foi possível carregar o detalhe por colaborador',

    // ANLT-602 — M-S3: avanço por segmento (task-02: colunas da tabela)
    SEGMENT_TABLE_TITLE: 'Avanço por grupo',
    SEGMENT_COL_GROUP: 'Grupo',
    SEGMENT_COL_ENROLLED: 'Colaboradores',
    SEGMENT_COL_ENROLLED_TOOLTIP: 'Colaboradores inscritos neste grupo.',
    SEGMENT_COL_PROGRESS: 'Avanço do ciclo',
    SEGMENT_COL_PROGRESS_TOOLTIP:
      'Etapas concluídas ÷ (inscritos do grupo × etapas do ciclo).',
    SEGMENT_STAGE_TOOLTIP: '% sobre os colaboradores desse grupo.',

    // ANLT-602 — M-S3 (task-03: cabeçalho/descrição/truncamento/nota/vazio sem filtro/erro)
    SEGMENT_HEADING: 'Avanço do ciclo por segmento',
    SEGMENT_DESCRIPTION: 'O quanto cada grupo avançou nas etapas do ciclo.',
    SEGMENT_TRUNCATED:
      'Mostrando os {{shown}} grupos com menor avanço, de {{total}} no total.',
    SEGMENT_NOTE:
      'Cada porcentagem é sobre os colaboradores desse grupo, não sobre o total da organização. Os grupos sem ninguém inscrito são mostrados com um traço.',
    SEGMENT_EMPTY_TITLE: 'Este processo ainda não tem inscritos',
    SEGMENT_EMPTY_DESCRIPTION:
      'Ainda não há grupos para mostrar neste processo.',
    SEGMENT_ERROR_TITLE: 'Não foi possível carregar o avanço por segmento',

    // ANLT-602 — M-S3 (task-04: seletor + vazio COM atributo escolhido — fecha a história)
    SEGMENT_SELECT_LABEL: 'Visualizar por',
    SEGMENT_SELECT_PLACEHOLDER: 'Escolha um atributo',
    SEGMENT_GROUP_STANDARD: 'Atributos padrão',
    SEGMENT_GROUP_CUSTOM: 'Campos personalizados',
    SEGMENT_GROUP_CUSTOM_TAG: 'Da sua empresa',
    SEGMENT_ATTRIBUTE_EMPTY_TITLE: 'Este atributo não retorna nenhum grupo',
    SEGMENT_ATTRIBUTE_EMPTY_DESCRIPTION:
      'Tente outro atributo, ou volte para o corte padrão.',

    // ANLT-606 (task-06) — filtro de situação (D-15). "BLOCK_*" é reutilizado
    // da ANLT-599 sem edição. DETAIL_FILTERED_EMPTY_* é a SEGUNDA causa de
    // vazio filtrado (a primeira é a busca, DETAIL_SEARCH_EMPTY_*): filtrar
    // por "sem metas carregadas" em um processo com inscritos também não é
    // "este processo não tem inscritos".
    DETAIL_FILTER_LABEL: 'Situação',
    DETAIL_FILTER_ALL: 'Todos os inscritos',
    DETAIL_FILTERED_EMPTY_TITLE: 'Nenhum colaborador nesta situação',
    DETAIL_FILTERED_EMPTY_DESCRIPTION:
      'Tente outra situação ou remova o filtro para ver todos os inscritos.',

    // ANLT-604 — M-S4: avanço por líder (task-02: colunas da tabela)
    LEADERS_TABLE_TITLE: 'Avanço por líder',
    LEADERS_SEARCH_PLACEHOLDER: 'Buscar por nome',
    LEADERS_COL_LEADER: 'Líder',
    LEADERS_COL_TEAM: 'Equipe',
    LEADERS_COL_PROGRESS: 'Avanço do ciclo',

    // ANLT-604 — M-S4: avanço por líder (task-03: seção, estados)
    LEADERS_HEADING: 'Avanço por líder',
    LEADERS_DESCRIPTION:
      'O quanto a equipe de cada líder avançou nas etapas do ciclo.',
    LEADERS_EMPTY_TITLE:
      'Este processo ainda não tem líderes com equipe inscrita',
    LEADERS_EMPTY_DESCRIPTION:
      'Quando houver colaboradores inscritos, seus líderes aparecerão aqui.',
    LEADERS_SEARCH_EMPTY_TITLE: 'Nenhum líder corresponde à sua busca',
    LEADERS_SEARCH_EMPTY_DESCRIPTION:
      'Tente outro nome, ou limpe a busca para ver todos os líderes.',
    LEADERS_ERROR_TITLE: 'Não foi possível carregar o avanço por líder',

    // ANLT-604 — M-S4: avanço por líder (task-04: colunas de etapa). Os rótulos de cada etapa
    // (STAGE_LOADED, etc.) são REUTILIZADOS de ANLT-599 via STAGE_LABEL_KEY — não redefinidos.
    LEADERS_NOTE:
      'Cada porcentagem é sobre a equipe inscrita desse líder. As etapas alcançadas e as pendentes são duas leituras distintas: nunca são somadas.',
    LEADERS_STAGE_TOOLTIP:
      'Acima, quantos da equipe alcançaram esta etapa. Abaixo, quantos ainda estão pendentes. Duas leituras distintas: nunca são somadas.',
    LEADERS_PENDING: '{{count}} pendente',
    LEADERS_PENDING_plural: '{{count}} pendentes',

    // ANLT-610 — M-R2: distribuição do cumprimento (semáforo, task-02:
    // cabeçalho + cinco estados + AC-3 "sem escala"). Este bloco conta
    // COLABORADORES, não metas (P-7, contrato v5) — a copy nunca diz "metas".
    DISTRIBUTION_HEADING: 'Distribuição do cumprimento',
    DISTRIBUTION_DESCRIPTION:
      'Quantos colaboradores caem em cada faixa de cumprimento da escala.',
    DISTRIBUTION_LOADING_LABEL: 'Carregando distribuição do cumprimento',
    DISTRIBUTION_ERROR_TITLE:
      'Não foi possível carregar a distribuição do cumprimento',
    DISTRIBUTION_EMPTY_TITLE:
      'Este processo ainda não tem colaboradores avaliados',
    DISTRIBUTION_EMPTY_DESCRIPTION:
      'Quando houver colaboradores com resultado, eles aparecerão aqui distribuídos por faixa.',
    DISTRIBUTION_NO_SCALE_TITLE:
      'Este ciclo não tem uma escala de cumprimento configurada',
    DISTRIBUTION_NO_SCALE_DESCRIPTION:
      'Configure uma escala de cumprimento para ver os colaboradores distribuídos por faixa.',

    // ANLT-610 — M-R2 (task-03: resolutores de banda + legenda da escala,
    // AC-1 metade legenda). O texto de cada Tag é `band.label` verbatim —
    // estas chaves cobrem apenas o rótulo da linha e a faixa. Sem «%»: o
    // SDL diz que o `upper_bound` da banda mais alta é o `max_scale` do
    // processo, que pode superar 100 — não é garantido um percentual.
    DISTRIBUTION_SCALE_LABEL: 'Escala de cumprimento',
    DISTRIBUTION_RANGE_BOTH: '{{lower}} – {{upper}}',
    DISTRIBUTION_RANGE_UP_TO: 'Até {{upper}}',
    DISTRIBUTION_RANGE_FROM: 'A partir de {{lower}}',

    // ANLT-610 — M-R2 (task-04: gráfico de barras empilhadas, AC-1 metade
    // gráfico). As três séries são semânticas (nunca por posição), e o eixo
    // Y conta COLABORADORES, nunca "metas" (P-7).
    DISTRIBUTION_SERIES_CRITICAL: 'Crítico',
    DISTRIBUTION_SERIES_COMPLIANT: 'Cumpre',
    DISTRIBUTION_SERIES_BELOW: 'Abaixo do esperado',
    DISTRIBUTION_AXIS_X: 'Faixa de cumprimento',
    DISTRIBUTION_AXIS_Y: 'Colaboradores',
    DISTRIBUTION_A11Y_CHART: 'Gráfico de distribuição do cumprimento',

    // ANLT-610 — M-R2 (task-05: nota de avaliados + nota "sem resultado" +
    // sub-estado "ninguém avaliado", AC-2, fecha o MR-B). "Sem resultado"
    // chega separado do backend e NUNCA é pintado como outra faixa — juntar
    // os dois transformaria "não foi avaliado" em "teve o pior resultado". A
    // nota não afirma que faixas + sem_resultado = inscritos
    // (`enrolled_count` não viaja neste payload, vive no M-R1).
    DISTRIBUTION_EVALUATED_NOTE:
      'As porcentagens são calculadas sobre {{count}} colaboradores avaliados.',
    DISTRIBUTION_WITHOUT_RESULT_TITLE: 'Sem resultado',
    DISTRIBUTION_WITHOUT_RESULT_BODY:
      '{{count}} colaboradores ainda não têm resultado: eles não fazem parte da escala. Não ter sido avaliado não é o mesmo que ter tirado zero.',
    DISTRIBUTION_NO_EVALUATED_TITLE: 'Ainda ninguém tem resultado',
    DISTRIBUTION_NO_EVALUATED_DESCRIPTION:
      'Quando houver colaboradores avaliados neste processo, você verá como eles se distribuem na escala.',

    // ANLT-609 (task-04) — banda de 5 tarjetas (M-R1)
    RESULTS_A11Y_BAND: 'Indicadores de resultados do processo',

    RESULTS_OFFICIAL_LABEL: 'Cumprimento oficial',
    RESULTS_OFFICIAL_TOOLTIP:
      'Percentual dos colaboradores avaliados que atingiu a faixa limite configurada pela sua organização. Conta pessoas: não é a média das notas, por isso não se move com um caso extremo.',
    RESULTS_OFFICIAL_DESCRIPTION:
      'Conta pessoas: {{compliant}} de {{evaluated}} avaliados atingiram o limite.',

    RESULTS_COMPLIANT_LABEL: 'Colaboradores que cumpriram',
    RESULTS_COMPLIANT_TOOLTIP:
      'Pessoas cujo resultado individual atingiu a faixa «{{band}}» ou superior, de acordo com as faixas configuradas pela sua organização.',
    RESULTS_COMPLIANT_DESCRIPTION:
      'De {{evaluated}} avaliados · faixa «{{band}}» ou superior.',
    RESULTS_COMPLIANT_DESCRIPTION_NO_BAND:
      'De {{evaluated}} avaliados · atingiram o limite.',

    RESULTS_CRITICAL_LABEL: 'Em estado crítico',
    RESULTS_CRITICAL_TOOLTIP:
      'Pessoas cujo resultado individual caiu na faixa mais baixa configurada pela sua organização. É o grupo que precisa de plano de ação.',
    RESULTS_CRITICAL_DESCRIPTION:
      'De {{evaluated}} avaliados · faixa «{{band}}».',
    RESULTS_CRITICAL_DESCRIPTION_NO_BAND:
      'De {{evaluated}} avaliados · na faixa mais baixa.',

    RESULTS_AVERAGE_LABEL: 'Cumprimento médio',
    RESULTS_AVERAGE_TOOLTIP:
      'Média dos resultados individuais dos avaliados. Faz média de notas: se move inteira com um caso extremo, por isso pode diferir do oficial sem que nenhum dos dois esteja errado.',
    RESULTS_AVERAGE_DESCRIPTION:
      'Faz média das notas de {{evaluated}} avaliados.',
    RESULTS_AVERAGE_DESCRIPTION_SCALE:
      'Faz média das notas de {{evaluated}} avaliados · escala 0–{{max}}.',

    RESULTS_COVERAGE_LABEL: 'Cobertura do processo',
    RESULTS_COVERAGE_TOOLTIP:
      'Percentual dos inscritos que terminou com um resultado registrado. Quem não foi avaliado não entra na média como zero: é contado à parte.',
    RESULTS_COVERAGE_DESCRIPTION:
      '{{evaluated}} avaliados de {{enrolled}} inscritos.',

    // ANLT-609 (task-05) — sección + estados (M-R1)
    RESULTS_HEADING: 'Resultados do processo',
    RESULTS_DESCRIPTION:
      'Como foi o desempenho da organização no ciclo encerrado. Aqui a palavra é cumprimento: o quanto foi alcançado de cada meta.',
    RESULTS_TRACK_RANGE: '{{start}} – {{end}}',
    RESULTS_EMPTY_TITLE: 'Este processo não tem inscritos',
    RESULTS_EMPTY_DESCRIPTION:
      'Quando houver colaboradores inscritos e avaliados, você verá aqui o resultado do processo.',
    RESULTS_ERROR_TITLE: 'Não foi possível carregar os resultados do processo',

    // ANLT-609 (task-07, MR-B) — micro-estado sem escala (AC-3)
    RESULTS_NO_SCALE_CARD: 'Sem escala configurada.',
    RESULTS_NO_SCALE_MESSAGE:
      'Este processo não tem faixas configuradas, portanto não há um limite a alcançar: o cumprimento oficial, os colaboradores que cumpriram e os críticos não podem ser calculados. Não são zero.',

    // ANLT-609 (task-08, MR-B) — declaração de não cobertos (AC-4)
    RESULTS_UNCOVERED_MESSAGE:
      '{{count}} colaboradores com metas não aparecem neste painel porque não têm um registro ativo. O relatório para download os inclui, agrupados em uma única linha sem nome.',

    // ANLT-617 (task-02) — `GoalsSegmentResultsBars`, linha de grupo (M-R3)
    RESULTS_GROUP_COVERAGE: '{{evaluated}} de {{total}} avaliados',
    RESULTS_GROUP_WITHOUT_RESULT: '{{count}} sem resultado',
    RESULTS_GROUP_AVERAGE_CAPTION: 'média',

    // ANLT-617 (task-03) — `GoalsSegmentResultsSection`, cabeçalho + 5 estados + totais (M-R3)
    RESULTS_GROUP_HEADING: 'Cumprimento por grupo',
    RESULTS_GROUP_DESCRIPTION:
      'Como o cumprimento se distribui entre as áreas, unidades ou cargos da sua organização.',
    RESULTS_GROUP_TOTALS:
      '{{groups}} grupos · {{enrolled}} inscritos · {{evaluated}} avaliados',
    RESULTS_GROUP_NOTE:
      'Faz a média de PESSOAS: o resultado individual de cada colaborador avaliado do grupo, na escala que sua organização configurou. Não é comparável a uma média que conta metas.',
    RESULTS_GROUP_TRUNCATED:
      'Mostramos {{shown}} de {{total}} grupos. Os que faltam não estão somados nesta lista.',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_TITLE: 'Este atributo não gera nenhum grupo',
    RESULTS_GROUP_ATTRIBUTE_EMPTY_DESCRIPTION:
      'O processo pode ter inscritos: o que não gera grupos é este corte. Tente outro atributo ou volte ao corte padrão.',
    RESULTS_GROUP_EMPTY_TITLE: 'Este processo não tem inscritos',
    RESULTS_GROUP_EMPTY_DESCRIPTION:
      'Quando houver colaboradores inscritos e avaliados, você verá aqui o corte por grupo.',
    RESULTS_GROUP_ERROR_TITLE:
      'Não foi possível carregar o cumprimento por grupo',

    // ANLT-617 (task-04) — `row-track`, a faixa empilhada (M-R3)
    RESULTS_GROUP_A11Y_BAR: 'Distribuição de {{group}} entre as faixas',

    // ANLT-617 (task-05) — micro-estado «sem escala» + nota de distribuição (M-R3)
    RESULTS_GROUP_NO_SCALE:
      'Sua organização não configurou faixas para este processo, então não há distribuição para mostrar. A média de cada grupo continua aparecendo.',
    RESULTS_GROUP_DISTRIBUTION_NOTE:
      'A barra mostra como cada grupo se distribui entre as faixas; o número é sua média. Dois grupos com a mesma média podem ter distribuições opostas.',

    // ANLT-614 (task-01) — `GoalsDisciplineSection`, cabeçalho + 5 estados + sub-estado
    // «ainda sem ciclos vencidos» (M-R5). Prefixo DISCIPLINE_, NUNCA RESULTS_ (esse prefixo
    // já tem seu próprio teste de paridade e não deve ser contaminado enquanto os ramos
    // paralelos de Resultados estão pela metade).
    DISCIPLINE_HEADING: 'Disciplina de acompanhamento durante o ciclo',
    DISCIPLINE_DESCRIPTION:
      'Quão disciplinado foi o processo de acompanhamento, não o resultado obtido.',
    DISCIPLINE_WHAT_IT_MEASURES:
      'Mede se a pessoa reportou todas as suas metas aprovadas em todos os ciclos já em curso. Não é a coluna «Cumpre» do relatório para download, que mede se o chefe avaliou.',
    DISCIPLINE_NO_CYCLES_TITLE: 'Ainda não há nada para medir',
    DISCIPLINE_NO_CYCLES_DESCRIPTION:
      'Nenhum ciclo deste processo venceu ainda. Quando o primeiro vencer, você verá aqui quem manteve o ritual de reportar.',
    DISCIPLINE_LOADING_LABEL: 'Carregando disciplina de acompanhamento',
    DISCIPLINE_ERROR_TITLE:
      'Não foi possível carregar a disciplina de acompanhamento',
    DISCIPLINE_EMPTY_TITLE: 'Este processo não tem inscritos',
    DISCIPLINE_EMPTY_DESCRIPTION:
      'Quando houver colaboradores inscritos, você verá aqui quem manteve o ritual de reportar suas metas.',

    // ANLT-614 (task-02) — `GoalsDisciplineKpiBand` + linha de ciclos medidos (AC-3, G-2/G-4).
    // A porcentagem pode ir para '—' enquanto os três contadores levam sua cifra real, incluindo
    // um 0 medido — causas distintas, declaradas separadamente (ver os tooltips).
    DISCIPLINE_OBSERVED_TRACKS: 'Medido sobre {{tracks}} ciclos já vencidos',
    DISCIPLINE_UP_TO_DATE_LABEL: 'Em dia',
    DISCIPLINE_UP_TO_DATE_TOOLTIP:
      'Reportou todas as suas metas aprovadas em todos os ciclos já em curso.',
    DISCIPLINE_PENDING_LABEL: 'Pendentes',
    DISCIPLINE_PENDING_TOOLTIP:
      'Tinham metas aprovadas para reportar e ainda devem algum acompanhamento.',
    DISCIPLINE_WITHOUT_GOALS_LABEL: 'Sem metas',
    DISCIPLINE_WITHOUT_GOALS_TOOLTIP:
      'Não tinham nenhuma meta aprovada: não contam nem como em dia nem como pendentes.',
    DISCIPLINE_WITHOUT_GOALS_DESCRIPTION: 'Não tinham nada para reportar',
    DISCIPLINE_PERCENTAGE_LABEL: '% de disciplina',
    DISCIPLINE_PERCENTAGE_TOOLTIP:
      'Sobre quem tinha metas aprovadas para reportar. Não inclui quem não tinha nada para reportar.',
    DISCIPLINE_PERCENTAGE_UNMEASURABLE:
      'Ninguém tinha metas aprovadas para reportar',
    DISCIPLINE_A11Y_BAND: 'Indicadores de disciplina de acompanhamento',

    // ANLT-614 (task-03) — `GoalsDisciplineGroupsChart` + `SegmentAttributeSelect` (AC-4,
    // G-5/G-7..G-10). O vazio do corte tem DUAS causas: com um atributo selecionado, a
    // mensagem NOMEIA os inscritos — NUNCA «este processo não tem inscritos», já dito a um
    // processo com 412 pessoas duas vezes nesta épica.
    DISCIPLINE_CUT_EMPTY_TITLE: 'Este corte não retornou grupos',
    DISCIPLINE_CUT_EMPTY_DESCRIPTION:
      'Há {{enrolled}} pessoas inscritas; tente outro atributo',
    DISCIPLINE_GROUPS_EMPTY_TITLE: 'Ainda não há grupos para mostrar',
    DISCIPLINE_NO_MEASURABLE_GROUPS:
      'Nenhum grupo tinha metas aprovadas para reportar',
    DISCIPLINE_GROUPS_EXCLUDED:
      'Sem barra no gráfico (ninguém tinha metas aprovadas para reportar): {{groups}}',
    DISCIPLINE_TRUNCATED: '{{shown}} de {{total}}',
    DISCIPLINE_CHART_SERIES_LABEL: '% de disciplina',
    // ANLT-615 (task-03) — `GoalsByCategorySection`, cabeçalho + 5 estados
    // (M-R4). Prefixo próprio `CATEGORY_`: nenhuma chave compartilhada com
    // `RESULTS_*`/`RESULTS_GROUP_*` — este bloco faz a média de METAS
    // (`average_goal_result`), não de pessoas.
    CATEGORY_HEADING: 'Cumprimento por categoria de meta',
    CATEGORY_UNIT_NOTE:
      'Este bloco faz a média de metas, não de pessoas: o resultado não é comparável aos indicadores individuais de outros blocos deste ciclo.',
    CATEGORY_UNIT_TOOLTIP:
      'Cada categoria faz a média do resultado das metas que a compõem, independentemente de quem as criou. Não é o mesmo número que o resultado individual de cada pessoa em outros blocos.',
    CATEGORY_LOADING_LABEL: 'Carregando cumprimento por categoria',
    CATEGORY_ERROR_TITLE:
      'Não foi possível carregar o cumprimento por categoria',
    CATEGORY_EMPTY_TITLE: 'Este processo não tem metas registradas',
    CATEGORY_EMPTY_DESCRIPTION:
      'Quando houver metas criadas para este processo, você verá aqui o cumprimento por categoria.',
    // ANLT-615 (task-04) — `GoalsByCategoryCut`, chaves internas do corte
    // (não dependem de a instância ser «estratégias» ou «enfoques»; essas
    // duas etiquetas são adicionadas pela task-05).
    CATEGORY_UNEVALUATED_TITLE: 'Ainda sem metas avaliadas',
    CATEGORY_CAP_NOTE: 'Mostrando {{shown}} de {{total}} categorias',
    CATEGORY_CUT_EMPTY: 'Este corte não tem categorias',
    CATEGORY_SERIES_AVERAGE: 'Média de cumprimento',
    // ANLT-615 (task-05) — conecta os dois cortes dentro da seção
    // (etiqueta de cada instância) + a nota de cobertura. Fecha a
    // história.
    CATEGORY_CUT_STRATEGIES: 'Meta estratégica',
    CATEGORY_CUT_APPROACHES: 'Enfoque',
    CATEGORY_UNCOVERED_NOTE:
      'Este cálculo não inclui {{count}} metas cujo responsável não tem um registro ativo. O relatório para download as inclui.',

    // ANLT-618 (task-01) — `GoalsRankingSection`, ranking de colaboradores
    // (M-R6). Prefixo próprio `RANKING_`, guarda de vocabulário próprio
    // (i18n/ranking-vocabulary.test.ts). `RANKING_COL_RESULT_TOOLTIP` diz
    // "nota", nunca "%" — `result` pode exceder 100 (max_scale, plano §3.1).
    // As chaves de detalhamento (`RANKING_GOALS_*`) são adicionadas por task-03.
    RANKING_HEADING: 'Ranking de colaboradores',
    RANKING_DESCRIPTION:
      'O resultado e a banda de cada pessoa inscrita neste processo, do melhor ao pior cumprimento.',
    RANKING_TABLE_TITLE: 'Ranking de colaboradores',
    RANKING_COL_COLLABORATOR: 'Colaborador',
    RANKING_COL_RESULT: 'Resultado',
    RANKING_COL_RESULT_TOOLTIP:
      'A nota da pessoa na escala configurada para este processo.',
    RANKING_COL_BAND: 'Banda',
    RANKING_NO_DATA_SR: 'Sem dado',
    RANKING_UNEVALUATED: 'Não avaliado',
    RANKING_NO_SCALE: 'Sem escala configurada',
    RANKING_EMPTY_TITLE: 'Este processo não tem inscritos',
    RANKING_EMPTY_DESCRIPTION:
      'Quando houver colaboradores inscritos neste processo, você verá o ranking aqui.',
    RANKING_ERROR_TITLE: 'Não foi possível carregar o ranking de colaboradores',

    // ANLT-618 (task-02) — busca, ordenação e colunas de atributo do
    // ranking. `RANKING_NO_BOSS` é o fallback VISÍVEL da coluna Chefe,
    // diferente do sr-only `RANKING_NO_DATA_SR` das outras quatro.
    RANKING_COL_HEADQUARTER: 'Sede',
    RANKING_COL_AREA: 'Área',
    RANKING_COL_POSITION: 'Cargo',
    RANKING_COL_BOSS: 'Chefe',
    RANKING_NO_BOSS: 'Sem chefe atribuído',
    RANKING_SEARCH_PLACEHOLDER: 'Buscar colaborador',
    RANKING_SEARCH_EMPTY_TITLE: 'Não encontramos ninguém com esse nome',
    RANKING_SEARCH_EMPTY_DESCRIPTION:
      'Tente outro nome ou verifique a ortografia.',

    // ANLT-618 (task-03) — `GoalsRankingBreakdown`, detalhamento por meta da
    // linha expandida. `RANKING_GOAL_WEIGHT` É um percentual (a
    // ponderação); `RANKING_GOAL_RESULT` é uma nota 0-100, nunca um "%"
    // (plano §8.5) — dois números, duas unidades, na mesma linha.
    RANKING_GOALS_TITLE: 'Detalhamento por meta',
    RANKING_GOAL_NAME: 'Meta',
    RANKING_GOAL_WEIGHT: 'Peso',
    RANKING_GOAL_RESULT: 'Resultado',
    RANKING_GOALS_EMPTY: 'Esta pessoa não tem metas registradas',
    RANKING_GOALS_ERROR: 'Não foi possível carregar o detalhamento de metas',
  },
};
